import { useCallback, useFeatures, useState, useEffect } from 'sb-hooks'
import { useConnect } from 'sb-redux-hook'
import { ModalVisibilityProps, openModal } from 'sb-modal'
import { useField } from 'formular'

import { constants, links } from 'common/helpers'
import actions from 'redux/actions'
import { openChangeFrequencySuccessModal, openChangeFrequencyFeedbackModal } from 'compositions/modals/changeFrequency'


export type ChangeFrequencyModalProps = Partial<Pick<ModalVisibilityProps, 'onClose'>> & {
  popup: string
  withFeedback?: boolean
}

const useChangeFrequencyModal = (props: ChangeFrequencyModalProps & ModalVisibilityProps) => {
  const { popup, withFeedback = false, closeModal, onClose } = props
  const { [constants.features.resubscribeReminderPopup]: isResubscribeReminderPopupEnabled } = useFeatures()

  const { isSubscriptionCancelled } = useConnect({
    isSubscriptionCancelled: 'models.subscription.isCancelled',
  })

  const isResubscribeFlow = isSubscriptionCancelled && isResubscribeReminderPopupEnabled
  const frequencyField = useField<string>({ value: '' })

  const [ frequencies, setFrequencies ] = useState<ChangeFrequencyModal.Frequency[]>(null)

  // load available frequencies
  useEffect(() => {
    let promise

    if (isResubscribeFlow) {
      promise = actions.models.subscription.fetchFrequenciesForResubscribeUser()
    }
    else {
      promise = actions.models.subscription.fetchFrequencies()
        .then((result) => result.filter(({ selected }) => !selected))
    }

    promise
      .then((result) => {
        setFrequencies((result))

        // set first option as selected
        frequencyField.set(result[0].name)
      })
      .catch(() => {
        closeModal()
      })
  }, [ isResubscribeFlow, frequencyField, closeModal ])

  // resubscribe flow
  const handleResubscribe = useCallback((selectedPlanName: string) => {
    actions.models.subscription.resubscribe({
      successModal: constants.modals.resubscribe.SuccessfullyResubscribed,
      plan: selectedPlanName,
      withErrorHandler: false,
      popup,
    })
      .then(() => {
        closeModal()
      }, ({ statusCode, error }) => {
        if (statusCode === '409' && error === 'change_plan') {
          closeModal()
          openModal('somethingWentWrong')
          return
        }

        // ATTN needed to call onClose callback
        onClose()

        actions.misc.cookies.removeItem(constants.cookieNames.offer)
        actions.misc.cookies.removeItem(constants.cookieNames.discountCoupon)

        actions.models.modals.close(constants.modals.resubscribe.GetBackTogether)
        actions.models.router.push(`${links.subscribe}?resubscribeText=true&planName=${selectedPlanName}`)
      })
  }, [ closeModal, popup, onClose ])

  // change frequency
  const handleChangePlan = useCallback((selectedPlan: ChangeFrequencyModal.Frequency) => {
    const { name, shippingPeriod, nextShippingDate } = selectedPlan

    actions.models.ui.showRequestLoader()
    actions.models.subscription.changePlan(name, popup)
      .finally(() => {
        actions.models.ui.hideRequestLoader()
        closeModal()
      })
      .then(() => {
        if (withFeedback) {
          openChangeFrequencyFeedbackModal()
          return
        }

        openChangeFrequencySuccessModal({
          nextShippingDate,
          shippingPeriod,
        })
      }, () => {
        openModal('somethingWentWrong')
      })
  }, [ closeModal, popup ])

  // apply frequency
  const handleConfirm = useCallback(() => {
    const selectedPlan = frequencies.find(({ name }) => name === frequencyField.state.value)

    if (!selectedPlan) {
      return
    }

    if (isResubscribeFlow) {
      handleResubscribe(selectedPlan.name)
      return
    }

    handleChangePlan(selectedPlan)
  }, [ frequencies, frequencyField, handleChangePlan, handleResubscribe, isResubscribeFlow ])

  return {
    frequencies,
    frequencyField,
    onConfirm: handleConfirm,
    onClose,
  }
}

export default useChangeFrequencyModal
